/**
 * Quantity Limit Manager
 * 
 * Handles maximum purchase quantity limits for products.
 * Store owners can set limits via product metafield: custom.max_purchase_quantity
 * 
 * When a customer tries to add more than the allowed quantity,
 * a modal pop-up appears with a message.
 */

class QuantityLimitManager {
  constructor() {
    this.modal = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.createModal();
    this.interceptAddToCart();
    this.interceptQuickAdd();
    this.interceptCartQuantityChanges();
  }

  /**
   * Creates the modal element for showing limit messages
   */
  createModal() {
    // Check if modal already exists
    if (document.getElementById('quantity-limit-modal')) {
      this.modal = document.getElementById('quantity-limit-modal');
      return;
    }

    const modalHTML = `
      <div id="quantity-limit-modal" class="qty-limit-modal" role="dialog" aria-modal="true" aria-hidden="true">
        <div class="qty-limit-modal__overlay"></div>
        <div class="qty-limit-modal__content">
          <button type="button" class="qty-limit-modal__close" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="qty-limit-modal__icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="22" stroke="currentColor" stroke-width="3"/>
              <path d="M24 14V26" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
              <circle cx="24" cy="33" r="2" fill="currentColor"/>
            </svg>
          </div>
          <h3 class="qty-limit-modal__title">Quantity Limit Reached</h3>
          <p class="qty-limit-modal__message"></p>
          <button type="button" class="qty-limit-modal__btn btn btn--primary">OK, Got it</button>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('quantity-limit-modal');

    // Add event listeners for closing
    this.modal.querySelector('.qty-limit-modal__overlay').addEventListener('click', () => this.closeModal());
    this.modal.querySelector('.qty-limit-modal__close').addEventListener('click', () => this.closeModal());
    this.modal.querySelector('.qty-limit-modal__btn').addEventListener('click', () => this.closeModal());

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.getAttribute('aria-hidden') === 'false') {
        this.closeModal();
      }
    });

    // Add styles
    this.addStyles();
  }

  /**
   * Add CSS styles for the modal
   */
  addStyles() {
    if (document.getElementById('quantity-limit-styles')) return;

    const styles = `
      .qty-limit-modal {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }
      
      .qty-limit-modal[aria-hidden="false"] {
        opacity: 1;
        visibility: visible;
      }
      
      .qty-limit-modal__overlay {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(2px);
      }
      
      .qty-limit-modal__content {
        position: relative;
        background: var(--color-background, #fff);
        padding: 2rem;
        border-radius: 12px;
        max-width: 400px;
        width: 90%;
        text-align: center;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
        transform: scale(0.9) translateY(20px);
        transition: transform 0.3s ease;
      }
      
      .qty-limit-modal[aria-hidden="false"] .qty-limit-modal__content {
        transform: scale(1) translateY(0);
      }
      
      .qty-limit-modal__close {
        position: absolute;
        top: 12px;
        right: 12px;
        background: none;
        border: none;
        padding: 8px;
        cursor: pointer;
        color: var(--color-foreground-secondary, #666);
        transition: color 0.2s ease;
      }
      
      .qty-limit-modal__close:hover {
        color: var(--color-foreground, #000);
      }
      
      .qty-limit-modal__icon {
        color: #e67e22;
        margin-bottom: 1rem;
      }
      
      .qty-limit-modal__title {
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0 0 0.75rem;
        color: var(--color-foreground, #000);
      }
      
      .qty-limit-modal__message {
        color: var(--color-foreground-secondary, #666);
        margin: 0 0 1.5rem;
        line-height: 1.5;
      }
      
      .qty-limit-modal__btn {
        min-width: 120px;
      }
    `;

    const styleEl = document.createElement('style');
    styleEl.id = 'quantity-limit-styles';
    styleEl.textContent = styles;
    document.head.appendChild(styleEl);
  }

  /**
   * Show the modal with a message
   * @param {string} message - The message to display
   */
  showModal(message) {
    if (!this.modal) return;
    
    this.modal.querySelector('.qty-limit-modal__message').textContent = message;
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    // Focus the OK button
    setTimeout(() => {
      this.modal.querySelector('.qty-limit-modal__btn').focus();
    }, 100);
  }

  /**
   * Close the modal
   */
  closeModal() {
    if (!this.modal) return;
    
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  /**
   * Get max quantity for a product/variant from the page
   * @param {string} variantId - The variant ID
   * @returns {number|null} - Max quantity or null if no limit
   */
  getMaxQuantity(variantId) {
    // Try to find max quantity data attribute on the page
    const productForm = document.querySelector(`product-form[data-variant-id="${variantId}"]`);
    if (productForm && productForm.dataset.maxQuantity) {
      return parseInt(productForm.dataset.maxQuantity, 10);
    }

    // Check for data on quick add buttons
    const quickAddBtn = document.querySelector(`.js-quick-add[data-variant-id="${variantId}"]`);
    if (quickAddBtn && quickAddBtn.dataset.maxQuantity) {
      return parseInt(quickAddBtn.dataset.maxQuantity, 10);
    }

    // Check for global product data
    const productData = document.querySelector(`[data-product-max-qty][data-variant-id="${variantId}"]`);
    if (productData && productData.dataset.productMaxQty) {
      return parseInt(productData.dataset.productMaxQty, 10);
    }

    // Check theme.maxQuantities if set globally
    if (window.theme && window.theme.maxQuantities && window.theme.maxQuantities[variantId]) {
      return window.theme.maxQuantities[variantId];
    }

    return null;
  }

  /**
   * Get current quantity in cart for a variant
   * @param {string} variantId - The variant ID
   * @returns {Promise<number>} - Current quantity in cart
   */
  async getCartQuantity(variantId) {
    try {
      const response = await fetch(`${window.theme?.routes?.cart || '/cart'}.js`);
      const cart = await response.json();
      const item = cart.items.find(item => item.variant_id.toString() === variantId.toString());
      return item ? item.quantity : 0;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return 0;
    }
  }

  /**
   * Check if adding quantity would exceed limit
   * @param {string} variantId - The variant ID
   * @param {number} quantityToAdd - Quantity being added
   * @param {number} maxQuantity - Maximum allowed quantity
   * @returns {Promise<{allowed: boolean, currentQty: number, message: string}>}
   */
  async checkLimit(variantId, quantityToAdd, maxQuantity) {
    const currentQty = await this.getCartQuantity(variantId);
    const newTotal = currentQty + quantityToAdd;

    if (newTotal > maxQuantity) {
      const message = `You can only buy ${maxQuantity} ${maxQuantity === 1 ? 'unit' : 'units'} of this item.`;
      return { allowed: false, currentQty, message };
    }

    return { allowed: true, currentQty, message: '' };
  }

  /**
   * Intercept product form submissions
   */
  interceptAddToCart() {
    // Listen for form submissions on product forms
    document.addEventListener('submit', (e) => {
      if (!(e.target instanceof Element)) return;

      const form = e.target.closest('.js-product-form');
      if (!form) return;

      // Skip if bypass flag is set (this is a re-submission after limit check passed)
      if (form.dataset.bypassLimitCheck === 'true') {
        return; // Allow this submission to proceed
      }

      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput) return;

      const variantId = variantInput.value;
      const quantityInput = form.querySelector('[name="quantity"]');
      const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

      // Get max quantity
      const productForm = form.closest('product-form');
      const maxQuantity = productForm?.dataset.maxQuantity 
        ? parseInt(productForm.dataset.maxQuantity, 10) 
        : this.getMaxQuantity(variantId);

      if (!maxQuantity) return; // No limit set

      // IMPORTANT: Prevent form submission IMMEDIATELY for products with limits
      e.preventDefault();
      e.stopImmediatePropagation();

      // Now do the async check
      this.checkLimit(variantId, quantity, maxQuantity).then(result => {
        if (!result.allowed) {
          this.showModal(result.message);
          
          // Reset button state
          const submitBtn = form.querySelector('[name="add"]');
          if (submitBtn) {
            submitBtn.classList.remove('is-loading');
            submitBtn.removeAttribute('aria-disabled');
          }
        } else {
          // Allowed - manually submit the form
          // Set bypass flag so our listener allows the resubmission
          form.dataset.bypassLimitCheck = 'true';
          
          // Dispatch a new submit event
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(submitEvent);
          
          // Clean up the bypass flag after a short delay
          setTimeout(() => {
            delete form.dataset.bypassLimitCheck;
          }, 500);
        }
      });
    }, true);
  }

  /**
   * Intercept quick add button clicks
   */
  interceptQuickAdd() {
    document.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;

      const quickAddBtn = e.target.closest('.js-quick-add');
      if (!quickAddBtn) return;
      
      // Skip if bypass flag is set (this is a re-click after limit check passed)
      if (quickAddBtn.dataset.bypassLimitCheck === 'true') {
        return; // Allow this click to proceed
      }

      const variantId = quickAddBtn.dataset.variantId;
      if (!variantId) return; // Multi-variant product, will use drawer

      const maxQuantity = quickAddBtn.dataset.maxQuantity 
        ? parseInt(quickAddBtn.dataset.maxQuantity, 10) 
        : this.getMaxQuantity(variantId);

      if (!maxQuantity) return; // No limit set

      // IMPORTANT: Prevent click IMMEDIATELY for products with limits
      e.preventDefault();
      e.stopImmediatePropagation();

      // Now do the async check
      this.checkLimit(variantId, 1, maxQuantity).then(result => {
        if (!result.allowed) {
          this.showModal(result.message);
          
          // Reset button state
          quickAddBtn.classList.remove('is-loading');
          quickAddBtn.removeAttribute('aria-disabled');
        } else {
          // Allowed - manually trigger the click
          quickAddBtn.dataset.bypassLimitCheck = 'true';
          quickAddBtn.click();
          
          // Clean up
          setTimeout(() => {
            delete quickAddBtn.dataset.bypassLimitCheck;
          }, 500);
        }
      });
    }, true);
  }

  /**
   * Intercept cart quantity changes
   */
  interceptCartQuantityChanges() {
    // Listen for quantity input changes in cart AND product cards
    document.addEventListener('change', (e) => {
      if (!(e.target instanceof Element)) return;

      const input = e.target.closest('.qty-input__input');
      if (!input) return;

      // Check if we're in a cart-item OR product-form
      const cartItem = input.closest('.cart-item');
      const productForm = input.closest('product-form');
      
      // If neither cart-item nor product-form, skip
      if (!cartItem && !productForm) return;

      let variantId;
      let maxQuantity;

      if (cartItem) {
        // Cart item context
        variantId = cartItem.dataset.variantId;
        maxQuantity = cartItem.dataset.maxQuantity 
          ? parseInt(cartItem.dataset.maxQuantity, 10) 
          : this.getMaxQuantity(variantId);
      } else if (productForm) {
        // Product card context
        variantId = productForm.dataset.variantId;
        maxQuantity = productForm.dataset.maxQuantity 
          ? parseInt(productForm.dataset.maxQuantity, 10) 
          : this.getMaxQuantity(variantId);
      }

      if (!maxQuantity) return;

      const newQuantity = parseInt(input.value, 10);

      if (newQuantity > maxQuantity) {
        e.preventDefault();
        e.stopImmediatePropagation();
        
        // Reset to max allowed
        input.value = maxQuantity;
        input.dataset.initialValue = maxQuantity;
        
        // Trigger a change event to update the cart with the capped value
        const changeEvent = new Event('change', { bubbles: true });
        input.dispatchEvent(changeEvent);
        
        this.showModal(`You can only buy ${maxQuantity} ${maxQuantity === 1 ? 'unit' : 'units'} of this item.`);
      }
    }, true);

    // Also intercept plus button clicks in cart AND product cards
    document.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;

      const plusBtn = e.target.closest('.qty-input__btn[name="plus"]');
      if (!plusBtn) return;

      const quantityInput = plusBtn.closest('quantity-input');
      if (!quantityInput) return;

      const input = quantityInput.querySelector('.qty-input__input');
      
      // Check if we're in a cart-item OR product-form
      const cartItem = quantityInput.closest('.cart-item');
      const productForm = quantityInput.closest('product-form');
      
      // If neither cart-item nor product-form, skip
      if (!cartItem && !productForm) return;

      let variantId;
      let maxQuantity;

      if (cartItem) {
        // Cart item context
        variantId = cartItem.dataset.variantId;
        maxQuantity = cartItem.dataset.maxQuantity 
          ? parseInt(cartItem.dataset.maxQuantity, 10) 
          : this.getMaxQuantity(variantId);
      } else if (productForm) {
        // Product card context
        variantId = productForm.dataset.variantId;
        maxQuantity = productForm.dataset.maxQuantity 
          ? parseInt(productForm.dataset.maxQuantity, 10) 
          : this.getMaxQuantity(variantId);
      }

      if (!maxQuantity) return;

      const currentQuantity = parseInt(input.value, 10);

      if (currentQuantity >= maxQuantity) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.showModal(`You can only buy ${maxQuantity} ${maxQuantity === 1 ? 'unit' : 'units'} of this item.`);
      }
    }, true);
  }
}

// Initialize the quantity limit manager
window.quantityLimitManager = new QuantityLimitManager();

